import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { createSelector } from 'reselect';
import UserDatabases from '@Services/UserDatabases';
import { nullIfFirstArgNegative } from '@Utils';
import { email as userEmail } from '@Containers/Header/HeaderComponentSelectors';

import {
  alertCreateDialogReducerKey,
  editMode,
  migrateMode,

  // frequencies
  FREQUENCY_WEEKLY,
  FREQUENCY_BIWEEKLY,
  FREQUENCY_MONTHLY,

  // process options
  PROCESS_OPTION_ZERO_RESULTS,
  PROCESS_OPTION_TITLE_AND_BIBLIO,
  PROCESS_OPTION_ABSTRACT,
  PROCESS_OPTION_FULL_ABSTRACT,
  PROCESS_OPTION_PARTIAL_ABSTRACT,
  PROCESS_OPTION_HIT_DETAILS,
} from './AlertCreateDialogConstatns';
import { getOption, isOptionChecked } from './processOptionsUtils';
import {
  validateEmptyInput,
  validateAlertNameLength,
  validateDuplicateAlertName,
  validateEmptyEmails,
  validateDatabases,
  validateEmptyDatabases,
  combineValidators,
} from './validationUtils';

export function alertCreateDialog(state) {
  return state.get(alertCreateDialogReducerKey);
}

export function mode(state, ownProps) {
  return ownProps.mode;
}

export function isEditMode(state, ownProps) {
  return mode(state, ownProps) === editMode;
}

export function isMigrateMode(state, ownProps) {
  return mode(state, ownProps) === migrateMode;
}

export function alertsInfo(state) {
  return alertCreateDialog(state).get('alertsInfo');
}

export function queryFactItem(state) {
  return alertCreateDialog(state).get('queryFactItem');
}

export function context(state) {
  return alertCreateDialog(state).get('context');
}

export function userTimeZone(state) {
  return alertCreateDialog(state).get('userTimeZone');
}

export function description(state) {
  return alertCreateDialog(state).get('description');
}

export function structures(state) {
  return alertCreateDialog(state).get('structures');
}

export function alertUuid(state) {
  return alertCreateDialog(state).get('alertUuid');
}

export function disableEditQuery(state) {
  return alertCreateDialog(state).get('disableEditQuery');
}

// original
export function original(state) {
  return alertCreateDialog(state).get('original');
}

export function originalAlertName(state) {
  return original(state).get('alertName');
}

export function originalDatabases(state) {
  return original(state).get('databases');
}

// form
export function alertForm(state) {
  return alertCreateDialog(state).get('form');
}

export function alertFormAlertName(state) {
  return alertForm(state).get('alertName');
}

export function alertFormEmails(state) {
  return alertForm(state).get('emails');
}

export function alertFormFrequency(state) {
  return alertForm(state).get('frequency');
}

export function alertFormDayOfWeek(state) {
  return alertForm(state).get('dayOfWeek');
}

export function alertFormDayOfMonth(state) {
  return alertForm(state).get('dayOfMonth');
}

export function alertFormTriggerMode(state) {
  return alertForm(state).get('triggerMode');
}

export function alertFormProcessOptions(state) {
  return alertForm(state).get('processOptions');
}

export function alertFormDatabases(state) {
  return alertForm(state).get('databases');
}

// emails array compatible with MultiInput
export const emailsMultiInput = createSelector([alertFormEmails, userEmail], (alertFormEmails, userEmail) => {
  return alertFormEmails.map((email) => ({
    value: email,
    disabled: email === userEmail,
  }));
});

// show non-owner email message
export function showNonOwnerEmailMessage(state) {
  return alertCreateDialog(state).get('showNonOwnerEmailMessage');
}

// frequency conditional dropdowns
export const alertFormDayOfWeekAvailable = createSelector([alertFormFrequency], (alertFormFrequency) => {
  return alertFormFrequency === FREQUENCY_WEEKLY || alertFormFrequency === FREQUENCY_BIWEEKLY;
});

export const alertFormDayOfMonthAvailable = createSelector([alertFormFrequency], (alertFormFrequency) => {
  return alertFormFrequency === FREQUENCY_MONTHLY;
});

// basic/advanced content databases
export const basicContentDatabases = createSelector([alertsInfo], (alertsInfo) => {
  if (!alertsInfo) return [];

  const { databases } = alertsInfo;
  const mapped = (databases || [])
    .filter((db) => !UserDatabases.isDefaultDatabase(db))
    .map((db) => ({
      value: db,
      label: UserDatabases.getDbName(db),
    }));

  return mapped;
});

export function basicContentAvailable(state) {
  const dbs = basicContentDatabases(state);
  return !isEmpty(dbs);
}

export const advancedContentDatabase = createSelector(
  [alertsInfo, alertFormDatabases],
  nullIfFirstArgNegative((alertsInfo, alertFormDatabases) => {
    const { databases } = alertsInfo;
    const defaultDb = find(databases, (db) => UserDatabases.isDefaultDatabase(db));

    return {
      value: defaultDb,
      label: UserDatabases.getDbName(defaultDb),
      checked: isOptionChecked(defaultDb, alertFormDatabases),
      disabled: false,
    };
  })
);

// defaults
export const defaultsTitleAndBiblio = createSelector(
  [alertsInfo],
  nullIfFirstArgNegative((alertsInfo) => {
    const { defaults, processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_TITLE_AND_BIBLIO, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_TITLE_AND_BIBLIO, defaults.processOptions);

    return { ...option, checked, disabled: false };
  })
);

export const defaultsAbstract = createSelector(
  [alertsInfo],
  nullIfFirstArgNegative((alertsInfo) => {
    const { defaults, processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_ABSTRACT, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_ABSTRACT, defaults.processOptions);

    return { ...option, checked, disabled: false };
  })
);

export const defaultsFullAbstract = createSelector(
  [alertsInfo],
  nullIfFirstArgNegative((alertsInfo) => {
    const { defaults, processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_FULL_ABSTRACT, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_FULL_ABSTRACT, defaults.processOptions);

    return { ...option, checked, disabled: false };
  })
);

export const defaultsPartialAbstract = createSelector(
  [alertsInfo],
  nullIfFirstArgNegative((alertsInfo) => {
    const { defaults, processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_PARTIAL_ABSTRACT, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_PARTIAL_ABSTRACT, defaults.processOptions);

    return { ...option, checked, disabled: false };
  })
);

export const defaultsHitDetails = createSelector(
  [alertsInfo],
  nullIfFirstArgNegative((alertsInfo) => {
    const { defaults, processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_HIT_DETAILS, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_HIT_DETAILS, defaults.processOptions);

    return { ...option, checked, disabled: false };
  })
);

// process options
export const processOptionZeroResults = createSelector(
  [alertsInfo, alertFormProcessOptions],
  nullIfFirstArgNegative((alertsInfo, alertFormProcessOptions) => {
    const { processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_ZERO_RESULTS, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_ZERO_RESULTS, alertFormProcessOptions);

    return { ...option, checked, disabled: false };
  })
);

export const processOptionTitleAndBiblio = createSelector(
  [alertsInfo, alertFormProcessOptions],
  nullIfFirstArgNegative((alertsInfo, alertFormProcessOptions) => {
    const { processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_TITLE_AND_BIBLIO, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_TITLE_AND_BIBLIO, alertFormProcessOptions);
    const disabled = isOptionChecked(PROCESS_OPTION_ABSTRACT, alertFormProcessOptions);

    return { ...option, checked, disabled };
  })
);

export const processOptionAbstract = createSelector(
  [alertsInfo, alertFormProcessOptions],
  nullIfFirstArgNegative((alertsInfo, alertFormProcessOptions) => {
    const { processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_ABSTRACT, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_ABSTRACT, alertFormProcessOptions);

    return { ...option, checked, disabled: false };
  })
);

export const processOptionFullAbstract = createSelector(
  [alertsInfo, alertFormProcessOptions],
  nullIfFirstArgNegative((alertsInfo, alertFormProcessOptions) => {
    const { processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_FULL_ABSTRACT, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_FULL_ABSTRACT, alertFormProcessOptions);

    return { ...option, checked, disabled: false };
  })
);

export const processOptionPartialAbstract = createSelector(
  [alertsInfo, alertFormProcessOptions],
  nullIfFirstArgNegative((alertsInfo, alertFormProcessOptions) => {
    const { processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_PARTIAL_ABSTRACT, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_PARTIAL_ABSTRACT, alertFormProcessOptions);

    return { ...option, checked, disabled: false };
  })
);

export const processOptionHitDetails = createSelector(
  [alertsInfo, alertFormProcessOptions],
  nullIfFirstArgNegative((alertsInfo, alertFormProcessOptions) => {
    const { processOptions } = alertsInfo;
    const option = getOption(PROCESS_OPTION_HIT_DETAILS, processOptions);
    const checked = isOptionChecked(PROCESS_OPTION_HIT_DETAILS, alertFormProcessOptions);

    return { ...option, checked, disabled: false };
  })
);

// advanced validation message
export const advancedValidationMessage = createSelector(
  [alertsInfo, originalDatabases, alertFormDatabases],
  nullIfFirstArgNegative((alertsInfo, originalDatabases, alertFormDatabases) => {
    const { isValid, validationMessage } = validateDatabases(alertFormDatabases, originalDatabases, alertsInfo.databases);

    return !isValid && validationMessage;
  })
);

// submit enabled
export const submitAlertFormEnabled = createSelector(
  [alertsInfo, alertFormAlertName, alertFormEmails, alertFormDatabases, isEditMode, originalAlertName],
  nullIfFirstArgNegative((alertsInfo, alertFormAlertName, alertFormEmails, alertFormDatabases, isEditMode, originalAlertName) => {
    const { isValid } = combineValidators(
      validateEmptyInput(alertFormAlertName),
      validateAlertNameLength(alertFormAlertName),
      validateDuplicateAlertName(alertFormAlertName, alertsInfo.alerts, isEditMode && originalAlertName),
      validateEmptyEmails(alertFormEmails),
      validateEmptyDatabases(alertFormDatabases)
    )({ combineLogic: '&&' });

    return isValid;
  })
);

// trackingType for AA
export function trackingType(state) {
  return alertCreateDialog(state).get('trackingType');
}
